import { FC } from 'react';
import { createBrowserRouter, Navigate, type RouteObject, useLocation } from 'react-router';

import RootLayout, { ErrorBoundary, HydrateFallback } from '@/layouts/root/RootLayout';
import { asRoute } from '@/routes/asRoute';
import AuthRoute from '@/routes/AuthRoute';
import GuestRoute from '@/routes/GuestRoute';

/** `<Navigate to="/path">` drops the query string, which some redirects carry meaning in. */
const RedirectWithParams: FC<{ to: string }> = ({ to }) => {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: to, search, hash }} replace />;
};

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    HydrateFallback,
    ErrorBoundary,
    children: [
      {
        element: <AuthRoute />,
        children: [
          {
            lazy: () => import('@/layouts/dashboard/DashboardLayout').then(asRoute),
            children: [
              {
                index: true,
                lazy: () => import('@/routes/home/index').then(asRoute),
                handle: { pageName: 'Dashboard', hideTitleBar: true },
              },
              {
                path: 'change-email/:token',
                lazy: () => import('@/routes/change-email/ChangeEmail').then(asRoute),
                handle: { hideTitleBar: true },
              },
              {
                path: 'close-account',
                lazy: () => import('@/routes/close-account/CloseAccount').then(asRoute),
                handle: { hideTitleBar: true },
              },
              { path: 'profile', lazy: () => import('@/routes/profile/Profile').then(asRoute) },
              {
                path: 'account/subscribe',
                lazy: () => import('@/routes/account/subscribe/AccountSubscribe').then(asRoute),
              },
              {
                path: 'account/unsubscribe',
                lazy: () => import('@/routes/account/unsubscribe/AccountUnsubscribe').then(asRoute),
              },
              {
                path: 'vote-pages',
                lazy: () => import('@/routes/vote-pages/VotePages').then(asRoute),
                handle: { pageName: 'Vote Pages', crumbs: [{ title: 'Vote Pages' }] },
              },
              {
                path: 'vote-pages/create',
                lazy: () => import('@/routes/vote-pages/create/CreateVotePage').then(asRoute),
                handle: { pageName: 'Create Vote Page' },
              },
              {
                path: 'payments',
                lazy: () => import('@/routes/payments/Payments').then(asRoute),
                handle: { pageName: 'Payments' },
              },
              {
                path: 'payments/setup',
                lazy: () => import('@/routes/payments/setup/PaymentsSetup').then(asRoute),
                handle: {
                  pageName: 'Get paid',
                  crumbs: [{ title: 'Payments', link: '/payments' }, { title: 'Get paid' }],
                },
              },
              // Was /payouts until the page became mostly about money coming in. Keep both redirects, remove much later
              { path: 'payouts', element: <RedirectWithParams to="/payments" /> },
              { path: 'payouts/setup', element: <RedirectWithParams to="/payments/setup" /> },
            ],
          },
          // Was /identities until every signed-in page had a voter list.
          { path: 'vote-pages/:id/identities', element: <Navigate to="../voters" relative="path" replace /> },
          {
            path: 'vote-pages/:id',
            lazy: () => import('@/layouts/vote-page/VotePageLayout').then(asRoute),
            handle: { showSideNav: true },
            children: [
              { index: true, element: <Navigate to="dashboard" replace /> },
              {
                path: 'dashboard',
                lazy: () => import('@/routes/vote-pages/vote-page/dashboard/VotePageDashboard').then(asRoute),
                handle: { pageName: 'Vote Page Dashboard' },
              },
              {
                path: 'manage',
                lazy: () => import('@/routes/vote-pages/vote-page/manage/VotePageManage').then(asRoute),
                handle: { pageName: 'Manage Vote Page' },
              },
              {
                path: 'manage/import',
                lazy: () => import('@/routes/vote-pages/vote-page/manage/import/VotePageImport').then(asRoute),
                handle: { pageName: 'Import nominees' },
              },
              {
                path: 'results',
                lazy: () => import('@/routes/vote-pages/vote-page/results/VotePageResult').then(asRoute),
                handle: { pageName: 'Results' },
              },
              {
                path: 'present',
                lazy: () => import('@/routes/vote-pages/vote-page/present/VotePagePresent').then(asRoute),
                handle: { pageName: 'Present', layoutLess: true },
              },
              {
                path: 'vote-payment',
                lazy: () => import('@/routes/vote-pages/vote-page/vote-payment/VotePagePayment').then(asRoute),
                handle: { pageName: 'Vote Payment' },
              },
              {
                path: 'voters',
                lazy: () => import('@/routes/vote-pages/vote-page/voters/VotePageVoters').then(asRoute),
                handle: { pageName: 'Voters' },
              },
              {
                path: 'nominations',
                lazy: () => import('@/routes/vote-pages/vote-page/nominations/VotePageNominations').then(asRoute),
                handle: { pageName: 'Vote Page Nominations' },
              },
              {
                path: 'embed',
                lazy: () => import('@/routes/vote-pages/vote-page/embed/VotePageEmbed').then(asRoute),
                handle: { pageName: 'Share & Embed' },
              },
              {
                path: 'settings',
                lazy: () => import('@/routes/vote-pages/vote-page/settings/VotePageSettings').then(asRoute),
                handle: { pageName: 'Vote Page Settings' },
              },
            ],
          },
        ],
      },
      {
        element: <GuestRoute />,
        children: [
          { path: 'login', lazy: () => import('@/routes/login/Login').then(asRoute) },
          { path: 'signup', lazy: () => import('@/routes/signup/SignUp').then(asRoute) },
          // Was /sign-up until 2026-10-31; links already shared still use it.
          { path: 'sign-up', element: <RedirectWithParams to="/signup" /> },
          {
            path: 'forgot-password',
            lazy: () => import('@/routes/forgot-password/ForgotPassword').then(asRoute),
          },
        ],
      },
      { path: 'password-reset/:token', lazy: () => import('@/routes/password-reset/PasswordReset').then(asRoute) },
      { path: '*', lazy: () => import('@/routes/not-found/NotFound').then(asRoute) },
    ],
  },
];

export const router = createBrowserRouter(routes);
